import { useState, useEffect, useRef } from 'react';
import { Star, X, Search, User, ChevronDown, Loader } from 'lucide-react';

const API_BASE = import.meta.env.VITE_API_BASE_URL || 'https://vmc-backend-vt2w.onrender.com/api';

const authHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
});

/**
 * AdminReviewFormDrawer
 * Right-side drawer for admin to submit a review or view existing reviews.
 *
 * Props:
 *  - businessId: string
 *  - businessName: string
 *  - mode: 'add' | 'view' (default: 'add')
 *  - reviewData: object | null (required when mode='view')
 *  - isOpen: boolean
 *  - onClose: () => void
 *  - onReviewSubmitted: () => void
 */
const AdminReviewFormDrawer = ({ businessId, businessName, mode = 'add', reviewData = null, isOpen, onClose, onReviewSubmitted }) => {
  // Consumer search/select
  const [consumers, setConsumers] = useState([]);
  const [consumersLoading, setConsumersLoading] = useState(false);
  const [consumerSearch, setConsumerSearch] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedConsumer, setSelectedConsumer] = useState(null);

  // Review form
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [experienceDate, setExperienceDate] = useState(new Date().toISOString().split('T')[0]);

  // Submission
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const dropdownRef = useRef(null);
  const searchTimeout = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Reset form when drawer opens/closes
  useEffect(() => {
    if (isOpen) {
      fetchConsumers('');
    } else {
      resetForm();
    }
  }, [isOpen]);

  // Debounced consumer search
  useEffect(() => {
    if (!isOpen) return;
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => fetchConsumers(consumerSearch), 300);
    return () => clearTimeout(searchTimeout.current);
  }, [consumerSearch]);

  const fetchConsumers = async (search = '') => {
    setConsumersLoading(true);
    try {
      const params = new URLSearchParams({
        limit: 50,
        offset: 0,
        is_active: true,
        sort_by: 'name',
        sort_order: 'asc',
      });
      if (search.trim()) params.append('search', search.trim());

      const res = await fetch(`${API_BASE}/admin/consumers?${params}`, { headers: authHeaders() });
      if (!res.ok) throw new Error('Failed to fetch consumers');
      const data = await res.json();
      setConsumers(data.data || []);
    } catch {
      setConsumers([]);
    } finally {
      setConsumersLoading(false);
    }
  };

  const resetForm = () => {
    setSelectedConsumer(null);
    setConsumerSearch('');
    setTitle('');
    setContent('');
    setRating(0);
    setHoveredRating(0);
    setExperienceDate(new Date().toISOString().split('T')[0]);
    setError('');
    setDropdownOpen(false);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
    });
  };

  const StarRow = ({ value }) => (
    <span className="inline-flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          size={20}
          className={i <= Math.round(value) ? 'fill-amber-400 text-amber-400' : 'fill-slate-100 text-slate-300'}
        />
      ))}
    </span>
  );

  const validate = () => {
    if (!selectedConsumer) return 'You must select a consumer on whose behalf to submit this review.';
    if (rating < 1) return 'Please select a star rating (1–5).';
    if (!title.trim() || title.trim().length < 5) return 'Title must be at least 5 characters.';
    if (!content.trim() || content.trim().length < 10) return 'Review content must be at least 10 characters.';
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationError = validate();
    if (validationError) { setError(validationError); return; }
    setError('');
    setSubmitting(true);

    try {
      const res = await fetch(`${API_BASE}/admin/businesses/${businessId}/reviews`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          consumer_id: selectedConsumer.id,
          title: title.trim(),
          content: content.trim(),
          rating,
          experience_date: experienceDate,
        }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to submit review');

      onReviewSubmitted?.();
      onClose();
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const displayRating = hoveredRating || rating;

  const ratingLabels = ['', 'Terrible', 'Poor', 'Average', 'Good', 'Excellent'];

  /* ── HEADER CONTENT ── */
  let headerTitle = 'Add Review';
  let headerSubtitle = `For: <span class="font-medium text-slate-700">${businessName}</span>`;
  
  if (mode === 'view' && reviewData) {
    headerTitle = 'Review Details';
  }

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/30 backdrop-blur-[1px] z-40 transition-opacity"
        onClick={onClose}
      />

      {/* Drawer */}
      <div className="fixed right-0 top-0 h-full w-[480px] bg-white shadow-2xl z-50 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 shrink-0 bg-white">
          <div>
            <h2 className="text-[15px] font-semibold text-slate-900">{headerTitle}</h2>
            {mode === 'add' && (
              <p className="text-[11px] text-slate-500 mt-0.5 truncate max-w-[340px]">
                For: <span className="font-medium text-slate-700">{businessName}</span>
              </p>
            )}
            {mode === 'view' && reviewData && (
              <p className="text-[11px] text-slate-500 mt-0.5">
                From: <span className="font-medium text-slate-700">{reviewData.reviewer?.name || 'Unknown'}</span>
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Scrollable body */}
        {mode === 'view' && reviewData ? (
          /* ── VIEW MODE ── */
          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
            {/* Star Rating */}
            <div>
              <h3 className="text-[12px] font-semibold text-slate-700 mb-3">Rating</h3>
              <div className="flex items-center gap-3">
                <StarRow value={reviewData.rating} />
                <span className="text-[13px] font-semibold text-slate-700">{reviewData.rating}.0 out of 5</span>
              </div>
            </div>

            {/* Title */}
            <div>
              <h3 className="text-[12px] font-semibold text-slate-700 mb-2">Title</h3>
              <p className="text-[13px] text-slate-800 leading-relaxed">{reviewData.title || '—'}</p>
            </div>

            {/* Content */}
            <div>
              <h3 className="text-[12px] font-semibold text-slate-700 mb-2">Review</h3>
              <p className="text-[13px] text-slate-700 leading-relaxed whitespace-pre-wrap">{reviewData.content || '—'}</p>
            </div>

            {/* Experience Date */}
            <div>
              <h3 className="text-[12px] font-semibold text-slate-700 mb-2">Experience Date</h3>
              <p className="text-[13px] text-slate-600">{formatDate(reviewData.experience_date)}</p>
            </div>

            {/* Reviewer Info */}
            <div className="pt-2 border-t border-slate-200 space-y-3">
              <div>
                <h3 className="text-[12px] font-semibold text-slate-700 mb-2">Reviewer</h3>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-slate-200 text-slate-600 text-[12px] font-bold flex items-center justify-center">
                    {reviewData.reviewer?.name?.charAt(0).toUpperCase() || '?'}
                  </div>
                  <div className="min-w-0">
                    <p className="text-[12px] font-medium text-slate-900">{reviewData.reviewer?.name || 'Unknown'}</p>
                    <p className="text-[11px] text-slate-400 truncate">{reviewData.reviewer?.email || ''}</p>
                  </div>
                </div>
              </div>

              {/* Source */}
              <div>
                <h3 className="text-[12px] font-semibold text-slate-700 mb-2">Source</h3>
                {reviewData.added_by_admin ? (
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-50 border border-amber-200 rounded-full text-[11px] font-semibold text-amber-700">
                    Admin-Submitted · {reviewData.admin_user?.name || 'Admin'}
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 rounded-full text-[11px] font-medium text-slate-600">
                    Direct Review
                  </span>
                )}
              </div>
            </div>

            {/* Created & Modified dates */}
            <div className="text-[11px] text-slate-400 space-y-1 pt-2 border-t border-slate-200">
              <p>Created: {formatDate(reviewData.created_at)}</p>
              {reviewData.updated_at && reviewData.updated_at !== reviewData.created_at && (
                <p>Last updated: {formatDate(reviewData.updated_at)}</p>
              )}
            </div>
          </div>
        ) : (
          /* ── ADD MODE ── */
          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">

          {/* ── SECTION 1: Consumer Selection ── */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-5 h-5 rounded-full bg-blue-600 text-white text-[10px] font-bold flex items-center justify-center shrink-0">1</div>
              <h3 className="text-[13px] font-semibold text-slate-800">Select Consumer <span className="text-red-500">*</span></h3>
              <span className="text-[10px] text-slate-400 ml-auto">Required — review is submitted on their behalf</span>
            </div>

            {selectedConsumer ? (
              /* Selected consumer pill */
              <div className="flex items-center gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="w-8 h-8 rounded-full bg-blue-600 text-white text-[12px] font-bold flex items-center justify-center shrink-0">
                  {selectedConsumer.name?.charAt(0).toUpperCase() || 'U'}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] font-semibold text-slate-900 truncate">{selectedConsumer.name}</p>
                  <p className="text-[11px] text-slate-500 truncate">{selectedConsumer.email}</p>
                </div>
                <button
                  onClick={() => { setSelectedConsumer(null); setDropdownOpen(true); }}
                  className="text-[11px] text-blue-600 hover:text-blue-700 font-medium shrink-0"
                >
                  Change
                </button>
              </div>
            ) : (
              /* Consumer search dropdown */
              <div ref={dropdownRef} className="relative">
                <div
                  className="flex items-center gap-2 px-3 py-2.5 border border-slate-300 rounded-lg cursor-text bg-white hover:border-blue-400 transition-colors focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500"
                  onClick={() => { setDropdownOpen(true); }}
                >
                  <Search size={14} className="text-slate-400 shrink-0" />
                  <input
                    type="text"
                    placeholder="Search consumer by name or email..."
                    value={consumerSearch}
                    onChange={(e) => { setConsumerSearch(e.target.value); setDropdownOpen(true); }}
                    className="flex-1 text-[13px] text-slate-800 placeholder-slate-400 outline-none bg-transparent"
                  />
                  {consumersLoading ? (
                    <Loader size={13} className="animate-spin text-slate-400 shrink-0" />
                  ) : (
                    <ChevronDown size={14} className="text-slate-400 shrink-0" />
                  )}
                </div>

                {dropdownOpen && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg z-10 max-h-[220px] overflow-y-auto">
                    {consumers.length === 0 ? (
                      <div className="px-4 py-3 text-[12px] text-slate-500 text-center">
                        {consumersLoading ? 'Searching...' : 'No active consumers found'}
                      </div>
                    ) : (
                      consumers.map((consumer) => (
                        <button
                          key={consumer.id}
                          type="button"
                          onClick={() => { setSelectedConsumer(consumer); setDropdownOpen(false); setConsumerSearch(''); }}
                          className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50 transition-colors text-left"
                        >
                          <div className="w-7 h-7 rounded-full bg-slate-200 text-slate-600 text-[11px] font-bold flex items-center justify-center shrink-0">
                            {consumer.name?.charAt(0).toUpperCase() || 'U'}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-[12px] font-medium text-slate-900 truncate">{consumer.name}</p>
                            <p className="text-[11px] text-slate-400 truncate">{consumer.email}</p>
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ── SECTION 2: Review Form ── */}
          <div className={!selectedConsumer ? 'opacity-50 pointer-events-none' : ''}>
            <div className="flex items-center gap-2 mb-4">
              <div className={`w-5 h-5 rounded-full text-white text-[10px] font-bold flex items-center justify-center shrink-0 ${selectedConsumer ? 'bg-blue-600' : 'bg-slate-300'}`}>2</div>
              <h3 className="text-[13px] font-semibold text-slate-800">Write Review</h3>
              {!selectedConsumer && <span className="text-[10px] text-slate-400 ml-2">Select a consumer first</span>}
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Star Rating */}
              <div>
                <label className="block text-[12px] font-semibold text-slate-700 mb-2">
                  Star Rating <span className="text-red-500">*</span>
                </label>
                <div className="flex items-center gap-2">
                  <div className="flex gap-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        onMouseEnter={() => setHoveredRating(star)}
                        onMouseLeave={() => setHoveredRating(0)}
                        onClick={() => setRating(star)}
                        className="p-0.5 transition-transform hover:scale-110"
                      >
                        <Star
                          size={24}
                          className={
                            star <= displayRating
                              ? 'fill-amber-400 text-amber-400'
                              : 'fill-slate-100 text-slate-300'
                          }
                        />
                      </button>
                    ))}
                  </div>
                  {displayRating > 0 && (
                    <span className="text-[12px] text-slate-600 font-medium">
                      {ratingLabels[displayRating]}
                    </span>
                  )}
                </div>
              </div>

              {/* Title */}
              <div>
                <label className="block text-[12px] font-semibold text-slate-700 mb-1.5">
                  Review Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => {
                    const filtered = e.target.value.replace(/[^a-zA-Z\s]/g, '');
                    setTitle(filtered);
                  }}
                  placeholder="Summarise the experience..."
                  maxLength={255}
                  className="w-full px-3 py-2 text-[13px] text-slate-800 placeholder-slate-400 border border-slate-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                />
                <p className="text-[10px] text-slate-400 mt-1 text-right">{title.length}/255</p>
              </div>

              {/* Content */}
              <div>
                <label className="block text-[12px] font-semibold text-slate-700 mb-1.5">
                  Review Content <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  placeholder="Describe the experience in detail..."
                  maxLength={5000}
                  rows={5}
                  className="w-full px-3 py-2 text-[13px] text-slate-800 placeholder-slate-400 border border-slate-300 rounded-lg resize-none focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                />
                <p className="text-[10px] text-slate-400 mt-1 text-right">{content.length}/5000</p>
              </div>

              {/* Experience Date */}
              <div>
                <label className="block text-[12px] font-semibold text-slate-700 mb-1.5">
                  Experience Date
                </label>
                <input
                  type="date"
                  value={experienceDate}
                  onChange={(e) => setExperienceDate(e.target.value)}
                  max={new Date().toISOString().split('T')[0]}
                  className="w-full px-3 py-2 text-[13px] text-slate-800 border border-slate-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                />
              </div>

              {/* Admin note */}
              {/* <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <svg className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-[11px] text-amber-700 leading-relaxed">
                  This review will be <strong>attributed to {selectedConsumer?.name || 'the selected consumer'}</strong> and marked as <strong>Admin-submitted</strong> for internal tracking.
                </p>
              </div> */}

              {/* Error */}
              {error && (
                <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <svg className="w-4 h-4 text-red-500 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-[12px] text-red-700">{error}</p>
                </div>
              )}
            </form>
          </div>
          </div>
        )}

        {/* Footer actions */}
        <div className="px-6 py-4 border-t border-slate-200 bg-white shrink-0 flex gap-3">
          {mode === 'view' ? (
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 text-[12px] font-semibold text-white bg-slate-600 rounded-lg hover:bg-slate-700 transition-colors"
            >
              Close
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                className="flex-1 px-4 py-2.5 text-[12px] font-semibold text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={submitting || !selectedConsumer}
                className="flex-1 px-4 py-2.5 text-[12px] font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {submitting ? (
                  <>
                    <Loader size={13} className="animate-spin" />
                    Submitting...
                  </>
                ) : (
                  'Submit Review'
                )}
              </button>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default AdminReviewFormDrawer;
